import { Ajax } from "../../api/infra";
import type { WorktimeImport } from "./model";

/** Connector for a single service in the staging API. */
export class StagingApiConnector<TDetails, TList> {

  /** Current JSON for upload */
  public currentJson = "";

  /** The current selected item */
  public current: TDetails | undefined;

  /** List of items after they have been loaded from the server. */
  public list: TList[] = [];

  /** Error message from save operation. */
  public error = "";

  constructor(protected serviceUrl: string, protected ajax: Ajax, private getBlank: () => TDetails) {
  }

  /** Sets the current item */
  public setCurrent(item: TDetails | undefined): void {
    if (!item) {
      this.currentJson = "";
      this.current = undefined;
      return;
    }
    this.current = item;
    this.currentJson = JSON.stringify(item, null, 2);
  }

  /** Shows the details view */
  public showDetails(id: string): void {
    this.ajax.getJSON(this.serviceUrl + "/" + id).then((result) => {
      this.setCurrent(result);
    });
  }

  /**
   * Gets an item by ID. This does not set the current item.
   * @param id Item ID to get.
   */
  public async getItem(id: string): Promise<TDetails> {
    return await this.ajax.getJSON(this.serviceUrl + "/" + id);
  }

  /**
   * Save any item to the server: Not connected to current item in any way.
   * @param item Item to save.
   */
  public async saveItem(item: TDetails): Promise<TDetails> {
    const result: TDetails = await this.ajax.postJSON(this.serviceUrl, item);
    return result;
  }

  /**
   * Saves changes to the current item.
   * @returns The saved item after save done.
   */
  public async saveCurrent(): Promise<TDetails> {
    const savedItem = await this.saveItem(this.current);
    this.setCurrent(savedItem);
    return savedItem;
  }

  /** Creates a new item */
  public createNew(): void {
    this.setCurrent(this.getBlank());
  }

  /**
   * Reloads the list. Also sets the current item to undefined.
   */
  public reloadList(): void {
    this.setCurrent(undefined);
    this.ajax.getJSON(this.serviceUrl).then((result) => {
      this.list = result.value;
    });
  }

  /** Saves the current JSON. */
  public saveJson(): void {
    this.ajax.postJSON(this.serviceUrl, JSON.parse(this.currentJson)).then((result) => {
      this.setCurrent(result);
      this.reloadList();
    }).catch((error) => {
      this.error = error.data || error;
    });
  }

  /** Deletes an item */
  public delete(id: string): void {
    this.ajax.remove(this.serviceUrl + "/" + id).then(() => {
      this.setCurrent(undefined);
      this.reloadList();
    });
  }

  /**
   * Calls the Update worktime method in the API (like the integrating services would).
   * @param worktimeImportData Data that is sent to the API.
   * This data may contain rows or employment relations or both and they are parsed, validated and saved in the API
   * as one set of rows employment. Employments are created / updated as necessary.
   */
  public async callUpdateWorktime(worktimeImportData: WorktimeImport): Promise<WorktimeImport> {
    const result: WorktimeImport = await this.ajax.postJSON(this.serviceUrl + "/update-worktime", worktimeImportData);
    return result;
  }

}